import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  CheckCircle, 
  Sparkles, 
  Users, 
  Target, 
  Trophy, 
  Smartphone, 
  Zap,
  Brain
} from "lucide-react";
import { LandingPageLayout } from "../shared/layout";

export function LandingPageScreen() {
  const { t } = useTranslation();
  
  return (
    <LandingPageLayout>
      {/* Hero Section */}
      <section className="relative py-20 md:py-32 overflow-hidden bg-gradient-to-b from-white to-gray-50 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 text-center relative z-10">
          <Badge variant="secondary" className="mb-6 px-4 py-1 text-sm rounded-full bg-primary/10 text-primary hover:bg-primary/20 border-primary/20">
            ✨ {t("landing.hero.badge")}
          </Badge>
          <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 bg-clip-text text-transparent bg-gradient-to-r from-gray-900 to-gray-600 dark:from-white dark:to-gray-300">
            {t("landing.hero.title")} <br className="hidden md:block" /> {t("landing.hero.titleHighlight")} <span className="text-primary">Rituely</span>
          </h1>
          <p className="text-xl text-gray-600 dark:text-gray-300 mb-10 max-w-2xl mx-auto leading-relaxed">
            {t("landing.hero.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" className="h-12 px-8 text-lg rounded-full shadow-lg hover:shadow-xl transition-all" asChild>
              <Link to="/register">{t("landing.hero.cta")}</Link>
            </Button>
            <Button size="lg" variant="outline" className="h-12 px-8 text-lg rounded-full" asChild>
              <Link to="/about">{t("landing.hero.learnMore")}</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">{t("landing.features.title")}</h2>
            <p className="text-lg text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              {t("landing.features.subtitle")}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-blue-50 to-white dark:from-gray-800 dark:to-gray-900">
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/50 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                  <Brain className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                </div>
                <CardTitle className="text-xl">{t("landing.features.aiAssistant.title")}</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-600 dark:text-gray-300">
                {t("landing.features.aiAssistant.description")}
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-green-50 to-white dark:from-gray-800 dark:to-gray-900">
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/50 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                  <Target className="h-6 w-6 text-green-600 dark:text-green-400" />
                </div>
                <CardTitle className="text-xl">{t("landing.features.habitTracking.title")}</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-600 dark:text-gray-300">
                {t("landing.features.habitTracking.description")}
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg hover:shadow-xl transition-all duration-300 bg-gradient-to-br from-purple-50 to-white dark:from-gray-800 dark:to-gray-900">
              <CardHeader>
                <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/50 rounded-2xl flex items-center justify-center mb-4 shadow-sm">
                  <Users className="h-6 w-6 text-purple-600 dark:text-purple-400" />
                </div>
                <CardTitle className="text-xl">{t("landing.features.tribes.title")}</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-600 dark:text-gray-300">
                {t("landing.features.tribes.description")}
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gamification Section */}
      <section className="py-24 bg-gray-50 dark:bg-gray-800 overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div className="order-2 md:order-1">
              <div className="relative">
                <div className="absolute -inset-4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full opacity-20 blur-3xl animate-pulse"></div>
                <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl p-8 relative border border-gray-100 dark:border-gray-700">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center">
                      <Trophy className="h-8 w-8 text-yellow-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-bold">{t("landing.gamification.levelAchieved")}</h3>
                      <p className="text-gray-500">{t("landing.gamification.xpEarned")}</p>
                    </div>
                  </div>
                  <div className="space-y-4">
                    <div className="flex justify-between text-sm font-medium">
                      <span>{t("landing.gamification.progressToLevel")}</span>
                      <span>75%</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-full w-3/4 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full"></div>
                    </div>
                    <div className="grid grid-cols-3 gap-4 mt-6">
                      {[1, 2, 3].map((i) => (
                        <div key={i} className="aspect-square rounded-xl bg-gray-50 dark:bg-gray-800 flex items-center justify-center border-2 border-dashed border-gray-200 dark:border-gray-700">
                          <div className="w-8 h-8 rounded-full bg-gray-200 dark:bg-gray-700"></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2">
              <Badge className="mb-4 bg-orange-100 text-orange-700 hover:bg-orange-200 border-none">{t("landing.gamification.badge")}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t("landing.gamification.title")}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {t("landing.gamification.subtitle")}
              </p>
              <ul className="space-y-4">
                {[
                  t("landing.gamification.feature1"),
                  t("landing.gamification.feature2"),
                  t("landing.gamification.feature3"),
                  t("landing.gamification.feature4")
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-gray-700 dark:text-gray-300">
                    <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Focus Mode Section */}
      <section className="py-24 bg-white dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4 bg-red-100 text-red-700 hover:bg-red-200 border-none">{t("landing.focus.badge")}</Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">{t("landing.focus.title")}</h2>
              <p className="text-lg text-gray-600 dark:text-gray-400 mb-8">
                {t("landing.focus.subtitle")}
              </p>
              <div className="grid grid-cols-2 gap-6">
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <Zap className="h-8 w-8 text-yellow-500 mb-3" />
                  <h4 className="font-bold mb-1">{t("landing.focus.pomodoroTitle")}</h4>
                  <p className="text-sm text-gray-500">{t("landing.focus.pomodoroDesc")}</p>
                </div>
                <div className="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                  <Sparkles className="h-8 w-8 text-purple-500 mb-3" />
                  <h4 className="font-bold mb-1">{t("landing.focus.statsTitle")}</h4>
                  <p className="text-sm text-gray-500">{t("landing.focus.statsDesc")}</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute -inset-4 bg-gradient-to-r from-red-500 to-pink-500 rounded-full opacity-20 blur-3xl"></div>
              <div className="bg-gray-900 text-white rounded-2xl shadow-2xl p-8 relative flex flex-col items-center justify-center aspect-square max-w-md mx-auto">
                <div className="w-48 h-48 rounded-full border-4 border-white/20 flex items-center justify-center relative mb-8">
                  <div className="absolute inset-0 border-4 border-red-500 rounded-full border-t-transparent rotate-45"></div>
                  <span className="text-5xl font-mono font-bold">25:00</span>
                </div>
                <div className="flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                    <div className="w-4 h-4 bg-white rounded-sm"></div>
                  </div>
                  <div className="w-12 h-12 rounded-full bg-red-500 flex items-center justify-center">
                    <div className="w-0 h-0 border-l-[10px] border-l-white border-y-[6px] border-y-transparent ml-1"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20"></div>
        <div className="container mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            {t("landing.cta.title")}
          </h2>
          <p className="text-xl opacity-90 mb-10 max-w-2xl mx-auto">
            {t("landing.cta.subtitle")}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Button size="lg" variant="secondary" className="h-14 px-8 text-lg font-semibold shadow-lg hover:shadow-xl transition-all" asChild>
              <Link to="/register">{t("landing.cta.getStarted")}</Link>
            </Button>
            <Button size="lg" variant="outline" className="h-14 px-8 text-lg bg-transparent border-white text-white hover:bg-white/10" asChild>
              <Link to="/pricing">{t("landing.cta.viewPricing")}</Link>
            </Button>
          </div>
          <p className="mt-6 text-sm opacity-70">
            {t("landing.cta.noCreditCard")}
          </p>
        </div>
      </section>
    </LandingPageLayout>
  );
}
